import hljs from "highlight.js/lib/common";
import { useMemo } from "react";

const EXT: Record<string, string> = {
  ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript", mjs: "javascript", go: "go", py: "python",
  rs: "rust", java: "java", kt: "kotlin", rb: "ruby", sh: "bash", bash: "bash", zsh: "bash", yml: "yaml", yaml: "yaml",
  json: "json", md: "markdown", css: "css", scss: "scss", html: "xml", xml: "xml", sql: "sql", c: "c", h: "c",
  cc: "cpp", cpp: "cpp", hpp: "cpp", cs: "csharp", php: "php", swift: "swift", toml: "ini", ini: "ini", mk: "makefile",
};

function language(path: string): string | undefined {
  const base = path.split("/").pop() ?? "";
  if (base === "Makefile") return "makefile";
  if (base === "Dockerfile") return "dockerfile";
  return EXT[base.split(".").pop()?.toLowerCase() ?? ""];
}

/** Highlight per line so line numbers stay aligned (multi-line tokens are rare enough). */
export function CodeView({ path, content }: { path: string; content: string }) {
  const lines = useMemo(() => {
    const lang = language(path);
    const raw = content.endsWith("\n") ? content.slice(0, -1).split("\n") : content.split("\n");
    if (!lang || !hljs.getLanguage(lang) || content.length > 500_000) return raw.map((l) => ({ html: null as string | null, text: l }));
    const html = hljs.highlight(content, { language: lang, ignoreIllegals: true }).value;
    // Re-split highlighted HTML on newlines, closing/reopening spans across lines.
    const out: Array<{ html: string | null; text: string }> = [];
    let open: string[] = [];
    for (const line of html.split("\n")) {
      const prefix = open.join("");
      const tags = line.match(/<span[^>]*>|<\/span>/g) ?? [];
      for (const t of tags) if (t === "</span>") open.pop(); else open.push(t);
      out.push({ html: prefix + line + "</span>".repeat(open.length), text: "" });
    }
    return out.slice(0, raw.length);
  }, [path, content]);
  const code = "whitespace-pre px-2.5";
  return (
    <div className="overflow-x-auto bg-background">
      <table className="w-full border-collapse font-mono text-xs leading-5">
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} id={`L${i + 1}`} className="target:[&>td]:bg-accent">
              <td className="w-px min-w-[50px] select-none px-2.5 text-right align-top text-faint">
                <a href={`#L${i + 1}`} className="text-inherit hover:text-foreground hover:no-underline">
                  {i + 1}
                </a>
              </td>
              {l.html !== null ? <td className={code} dangerouslySetInnerHTML={{ __html: l.html }} /> : <td className={code}>{l.text}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
